import { supabase } from '@/integrations/supabase/client';

/**
 * Service for migrating images from temporary /lovable-uploads/ to permanent Supabase Storage
 */
export class ImageMigrationService {
  private static readonly PERMANENT_BUCKET = 'photos';

  /**
   * Migrate an image from /lovable-uploads/ to permanent Supabase Storage
   * Never migrates /images/ URLs as these are permanent assets
   */
  static async migrateImage(tempImageUrl: string): Promise<string | null> {
    // Skip permanent URLs from /images/ folder
    if (!tempImageUrl || tempImageUrl.startsWith('/images/')) {
      console.log('[ImageMigration] URL is a permanent asset in /images/, skipping:', tempImageUrl);
      return tempImageUrl; // Return original if it's a permanent asset
    }
    
    if (!tempImageUrl.includes('/lovable-uploads/')) {
      console.log('[ImageMigration] URL is not from lovable-uploads, skipping:', tempImageUrl);
      return tempImageUrl; // Return original if not a temp upload
    }

    try {
      console.log('[ImageMigration] Starting migration for:', tempImageUrl);

      // Extract filename from the temp URL
      const urlParts = tempImageUrl.split('/');
      const tempFilename = urlParts[urlParts.length - 1];
      const fileExt = tempFilename.split('.').pop()?.toLowerCase() || 'png';
      
      // Create new permanent filename
      const permanentFilename = `migrated-${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

      // Download the image from the temp URL
      const response = await fetch(tempImageUrl);
      if (!response.ok) {
        console.error('[ImageMigration] Failed to fetch temp image:', response.statusText);
        return null;
      }

      const blob = await response.blob();
      console.log('[ImageMigration] Downloaded temp image, size:', blob.size);

      // Determine MIME type
      const getMimeType = (extension: string) => {
        const mimeTypes: { [key: string]: string } = {
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif',
          'webp': 'image/webp'
        };
        return mimeTypes[extension] || 'image/jpeg';
      };

      const contentType = getMimeType(fileExt);
      console.log('[ImageMigration] Using content type:', contentType);

      // Upload to permanent Supabase Storage
      const { data, error } = await supabase.storage
        .from(this.PERMANENT_BUCKET)
        .upload(permanentFilename, blob, {
          cacheControl: '3600',
          upsert: false,
          contentType: contentType
        });

      if (error) {
        console.error('[ImageMigration] Upload error:', error);
        return null;
      }

      console.log('[ImageMigration] Upload successful:', data);

      // Get permanent public URL
      const { data: publicUrlData } = supabase.storage
        .from(this.PERMANENT_BUCKET)
        .getPublicUrl(permanentFilename);

      // Ensure the URL has the correct "/object" segment
      const permanentUrl = publicUrlData.publicUrl.includes('/object/') 
        ? publicUrlData.publicUrl 
        : publicUrlData.publicUrl.replace('/storage/v1/public/', '/storage/v1/object/public/');

      console.log('[ImageMigration] Migration successful:', {
        from: tempImageUrl,
        to: permanentUrl
      });

      return permanentUrl;

    } catch (error) {
      console.error('[ImageMigration] Migration error:', error);
      return null;
    }
  }

  /**
   * Migrate photo URLs for a specific interview
   */
  static async migrateInterviewImages(interviewId: string): Promise<boolean> {
    try {
      console.log('[ImageMigration] Migrating images for interview:', interviewId);

      // Get current interview data including article_type
      const { data: interview, error: fetchError } = await supabase
        .from('interviews')
        .select('photo_url, context_image_url, article_type, interviewee_name')
        .eq('id', interviewId)
        .maybeSingle();

      if (fetchError) {
        console.error('[ImageMigration] Error fetching interview:', fetchError);
        return false;
      }

      if (!interview) {
        console.error('[ImageMigration] Interview not found:', interviewId);
        return false;
      }

      // CRITICAL: Never migrate Briefr Bullets or Kai articles - they use permanent images
      const isBriefBullets = interview.article_type && (
        interview.article_type === 'brief_bullets' ||
        interview.article_type === 'brief_bullets_natt' ||
        interview.article_type === 'brief_bullets_dag'
      );
      
      const isKaiArticle = interview.interviewee_name?.toLowerCase().includes('kai');

      if (isBriefBullets || isKaiArticle) {
        console.log('[ImageMigration] Skipping migration for Briefr Bullets or Kai article - uses permanent images');
        return true; // Return success without migrating
      }

      let updatedData: any = {};
      let hasUpdates = false;

      // Migrate photo_url if it's a temp upload
      if (interview.photo_url && interview.photo_url.includes('/lovable-uploads/')) {
        console.log('[ImageMigration] Migrating photo_url:', interview.photo_url);
        const newPhotoUrl = await this.migrateImage(interview.photo_url);
        if (newPhotoUrl) {
          updatedData.photo_url = newPhotoUrl;
          hasUpdates = true;
          console.log('[ImageMigration] Photo URL migrated successfully');
        }
      }

      // Migrate context_image_url if it's a temp upload
      if (interview.context_image_url && interview.context_image_url.includes('/lovable-uploads/')) {
        console.log('[ImageMigration] Migrating context_image_url:', interview.context_image_url);
        const newContextImageUrl = await this.migrateImage(interview.context_image_url);
        if (newContextImageUrl) {
          updatedData.context_image_url = newContextImageUrl;
          hasUpdates = true;
          console.log('[ImageMigration] Context image URL migrated successfully');
        }
      }

      // Update the interview with migrated URLs
      if (hasUpdates) {
        const { error: updateError } = await supabase
          .from('interviews')
          .update(updatedData)
          .eq('id', interviewId);

        if (updateError) {
          console.error('[ImageMigration] Error updating interview URLs:', updateError);
          return false;
        }

        console.log('[ImageMigration] Interview URLs updated successfully:', updatedData);
      } else {
        console.log('[ImageMigration] No images to migrate for interview:', interviewId);
      }

      return true;

    } catch (error) {
      console.error('[ImageMigration] Error migrating interview images:', error);
      return false;
    }
  }

  /**
   * Batch migrate all interviews with temporary image URLs
   */
  static async migrateAllTempImages(): Promise<void> {
    try {
      console.log('[ImageMigration] Starting batch migration of all temp images');

      // Find all interviews with lovable-uploads URLs, excluding Briefr Bullets
      const { data: interviews, error } = await supabase
        .from('interviews')
        .select('id, photo_url, context_image_url, article_type, interviewee_name')
        .or('photo_url.like.%/lovable-uploads/%,context_image_url.like.%/lovable-uploads/%')
        .not('article_type', 'in', '(brief_bullets,brief_bullets_natt,brief_bullets_dag)');

      if (error) {
        console.error('[ImageMigration] Error fetching interviews with temp images:', error);
        return;
      }

      if (!interviews || interviews.length === 0) {
        console.log('[ImageMigration] No interviews with temp images found');
        return;
      }

      console.log(`[ImageMigration] Found ${interviews.length} interviews with temp images`);

      // Migrate each interview
      for (const interview of interviews) {
        console.log(`[ImageMigration] Processing interview ${interview.id}`);
        await this.migrateInterviewImages(interview.id);
        
        // Add small delay to avoid overwhelming the storage service
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      console.log('[ImageMigration] Batch migration completed');

    } catch (error) {
      console.error('[ImageMigration] Batch migration error:', error);
    }
  }
}